import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type AcknowledgementDefinitionFormSchema,
	acknowledgementDefinitionSchema,
} from "@msuite/utils/schemas";
import type { Enums } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { AcknowledgementDefinitionForm } from "@/forms/acknowledgement-definition-form";
import { client } from "@/hono";

/** Props Interface */
interface AddAcknowledgementDefinitionModalProps extends ModalProps {
	worksite?: Enums<"attendanceWorksiteType">;
	projectId?: string;
	onComplete: (() => Promise<void>) | undefined;
}

export function AddAcknowledgementDefinitionModal({
	isOpen,
	onClose,
	worksite,
	projectId,
	onComplete,
}: AddAcknowledgementDefinitionModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<AcknowledgementDefinitionFormSchema>({
		resolver: resolver(acknowledgementDefinitionSchema),
		defaultValues: {
			state: "active",
			worksite,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: AcknowledgementDefinitionFormSchema) {
		const response = await client.organizations["acknowledgement-definitions"].$post({
			json: { ...json, projectId },
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("organization.worksite_acknowledgement_definitions.add")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, console.error)}
			>
				<Modal.Body>
					<AcknowledgementDefinitionForm showWorksiteField={!worksite} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
